<template>
    <div>
        <img :src="model.logo" aria-hidden="true" alt="decorative-drops-icon" />
        <div class="container-fluid">
            <div class="row justify-content-between align-items-baseline">
                <a class="skip-main" href="#main">{{ model.skipToContent }}</a>
                <div class="col">
                    <a :href="model.startPageUrl" :aria-label="model.logoText" class="hsy-icon">
                        <svg class="icon icon-logo-text"><use xlink:href="#icon-logo-text"></use></svg>
                    </a>
                </div>
                <nav class="quick-links" :aria-label="model.quickLinksHeading">
                    <ul class="d-flex">
                        <li v-for="(shortcut, index) in model.quickLinks" :key="index + 'sc'" class="col-auto">
                            <a :href="shortcut.url" :class="{active: shortcut.active}">{{ shortcut.name }}</a>
                        </li>
                    </ul>
                </nav>
                <nav class="lang-select" :aria-label="model.languageSelectHeading">
                    <ul class="d-flex">
                        <li v-for="(lang, index) in model.languages" :key="index + 'l'" class="col-auto">
                            <a v-if="lang.active" :href="lang.href" :aria-label="lang.label" :class="lang.active" aria-current="page" class="text-uppercase">{{ lang.title }}</a>
                            <a v-else :href="lang.href" :aria-label="lang.label" class="text-uppercase">{{ lang.title }}</a>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
        <nav :aria-label="model.mainNavigationHeading">
            <div class="container-fluid mt-0">
                <ul class="main-nav d-flex " :class="model.isMiniSite ? 'flex-wrap' : ''">
                    <li v-for="(navItem, index) in model.mainNavigation" 
                        :key="index"
                        class="col-auto">
                        <button v-if="navItem.isCurrentPage" 
                            :class="{active: navItem.active}"
                            class="main-nav--section" 
                            :href="navItem.url"
                            aria-current="page"
                            :aria-expanded="currentSubNav === navItem ? true : false"
                            aria-haspopup="true"
                            @click.prevent="setSubnaviStructure(navItem)"
                            @keydown.enter.prevent="setSubnaviStructure(navItem)">{{ navItem.name }}</button>
                        <button v-else 
                            :class="{active: navItem.active}"
                            class="main-nav--section" 
                            :aria-label="navItem.name" 
                            :href="navItem.url"
                            :aria-expanded="currentSubNav !== null ? true : false"
                            aria-haspopup="true"
                            @click.prevent="setSubnaviStructure(navItem)"
                            @keydown.enter.prevent="setSubnaviStructure(navItem)">{{ navItem.name }}</button>
                        <section 
                            class="main-nav--first-level container-fluid mt-0"
                            v-if="currentSubNav === navItem">

                            <div v-if="currentSubNav.navigationPreview.heading !== null || currentSubNav.navigationPreview.introText !== null" class="container-fluid mt-0">
                                <a :href="currentSubNav.url" class="">
                                    <h2 class="main-nav--first-level-heading" tabindex="0">
                                        {{ currentSubNav.navigationPreview.heading }}

                                        <span v-if="currentSubNav.url" :href="currentSubNav.url" :aria-label="currentSubNav.navigationPreview.ctaLinkText" class="main-nav--first-level-link">
                                            <svg class="icon icon-hsy-icon-arrow-right-short" :alt="model.searchBar.langSearchLabel">
                                                <use role="presentation" xlink:href="#icon-hsy-icon-arrow-right-short"></use>
                                            </svg>
                                        </span>
                                    </h2>

                                </a>
                            </div>

                            <div class="container-fluid mt-0">
                                <section class="row">
                                    <div class="col-lg-5 main-nav--second-level-container">
                                        <ul>
                                            <li v-for="(navItem, index) in navItem.subLinks" :key="index" class="main-nav--second-level-list-item">
                                                <a :href="navItem.url" class="main-nav--second-level-link" :class="currentSub3rdLvlNav === navItem.subLinks || navItem.active === true ? 'is-open' :''">{{ navItem.name }}</a>
                                                <button v-if="navItem.subLinks.length > 0" class="unstyled-button icon-button" @click="set3rdLvlNav(navItem)" :aria-label="navItem.name" :aria-haspopup="navItem.subLinks.length > 0 ? true : false" :aria-expanded="currentSub3rdLvlNav !== null">
                                                    <svg v-if="currentSub3rdLvlNav === null || currentSub3rdLvlNav !== navItem.subLinks" class="icon icon-hsy-icon-plus-bold">
                                                        <use role="presentation" xlink:href="#icon-hsy-icon-plus-bold"></use>
                                                    </svg>
                                                    <svg v-else class="icon icon-hsy-icon-minus-bold">
                                                        <use role="presentation" xlink:href="#icon-hsy-icon-minus-bold"></use>
                                                    </svg>
                                                </button>
                                                <div v-if="currentSub3rdLvlNav !== null && currentSub3rdLvlNav === navItem.subLinks" class="main-nav--third-level-container">
                                                    <ul>
                                                        <li v-for="(navItem, index) in currentSub3rdLvlNav" :key="index" class="main-nav--third-level-list-item">
                                                            <a :href="navItem.url" class="main-nav--third-level-link" :class="(currentSub4thLvlNav === navItem.subLinks || navItem.active === true ? 'is-open ' : '') + (navItem.isCurrentPage === true ? 'is-active' : '')">{{ navItem.name }}</a>
                                                            <button v-if="navItem.subLinks && navItem.subLinks.length > 0" class="unstyled-button icon-button" @click="set4thLvlNav(navItem)" :aria-label="navItem.name" :aria-haspopup="navItem.subLinks.length > 0 ? true : false" :aria-expanded="currentSub4thLvlNav !== null">
                                                                <svg v-if="currentSub4thLvlNav === null || currentSub4thLvlNav !== navItem.subLinks" class="icon icon-hsy-icon-plus-bold">
                                                                    <use role="presentation" xlink:href="#icon-hsy-icon-plus-bold"></use>
                                                                </svg>
                                                                <svg v-else class="icon icon-hsy-icon-minus-bold">
                                                                    <use role="presentation" xlink:href="#icon-hsy-icon-minus-bold"></use>
                                                                </svg>
                                                            </button>
                                                            <div v-if="currentSub4thLvlNav !== null && currentSub4thLvlNav === navItem.subLinks" class="main-nav--third-level-container">
                                                                <ul>
                                                                    <li v-for="(navItem, index) in currentSub4thLvlNav" :key="index" class="main-nav--third-level-list-item">
                                                                        <a :href="navItem.url" class="main-nav--third-level-link" :class="navItem.isCurrentPage === true ? 'is-active' : ''">{{ navItem.name }}</a>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </section>
                            </div>                            
                        </section>
                    </li>
                </ul>
                <transition name="fade">
                    <search-overlay v-if="showOverlay" :model="model.searchBar"></search-overlay>
                </transition>
                <form class="col-auto nav-search pr-0" role="search" @keyup.enter="showOverlay" @click="showOverlay">
                    <button aria-haspopup="true" type="button" class="unstyled-button pr-0" :aria-expanded="overlayVisible ? 'true' : 'false'">
                        <span class="d-none d-lg-inline search-label">{{ model.searchBar.langSearchLabel }}</span>
                        <svg class="icon icon-hsy-icon-search-bold ml-2" :alt="model.searchBar.langSearchLabel">
                            <use role="presentation" xlink:href="#icon-hsy-icon-search-bold"></use>
                        </svg>
                    </button>
                </form>
            </div>
            
        </nav> 
    </div>
</template>
<script>
    import searchOverlay from './search-overlay.vue';
    import { mapState } from 'vuex';

    export default {
        components: {
            'search-overlay': searchOverlay
        },
        props: {
            model: {
                type: Object,
                required: true
            }
        },

        data: () => {
            return {
                linkList: [],
                currentSubNav: null,
                currentSub3rdLvlNav: null,
                currentSub4thLvlNav: null,
            }
        },
        computed: mapState({
            accordionOpen: state => state.accordionOpen,
            overlayVisible: state => state.showOverlay
        }),
        mounted: function () {
            setTimeout(() => {
                this.goToAnchorInUrl();
            }, 500)
            const vueScope = this;
            document.body.addEventListener("click", function(e) {
                if(!e.target.closest('.main-nav')) {
                    if(vueScope.currentSubNav !== null && vueScope.currentSubNav.active === true) {
                        vueScope.currentSubNav.active = false;
                        vueScope.currentSubNav = null;
                        vueScope.currentSub3rdLvlNav = null;
                        vueScope.currentSub4thLvlNav = null;
                    }
                }
            });
        },
        destroyed: function () {
            document.body.removeEventListener("click");
        },
        methods: {
            setSubnaviStructure(structure) {
                const hasStructure = structure.subLinks.length > 0;
                if(hasStructure === false) {
                    // No subitems, might be a minisite
                    window.location.href = structure.url;
                    return; 
                }
                if(this.currentSubNav === structure) {
                    // Same main item clicked again, close this submenu
                    this.currentSubNav.active = false;
                    this.currentSubNav = null;
                    return;
                } else {
                    if(this.currentSubNav !== null) this.currentSubNav.active = false;
                    this.currentSubNav = structure;
                    this.currentSubNav.active = true;
                }
            },
            set3rdLvlNav(navItem) {
                if(this.currentSub3rdLvlNav === navItem.subLinks) {
                    this.currentSub3rdLvlNav = null;
                    this.currentSub4thLvlNav = null;
                    return;
                }
                this.currentSub3rdLvlNav = navItem.subLinks;
                this.currentSub4thLvlNav = null;
            },
            set4thLvlNav(navItem) {
                if(this.currentSub4thLvlNav === navItem.subLinks) {
                    this.currentSub4thLvlNav = null;
                    return;
                }
                this.currentSub4thLvlNav = navItem.subLinks;
            },
            handleClickOutside(event) {
                if(!event.target.closest('.main-nav--first-level')) {
                    this.currentSubNav = null;
                }
            },
            handleKeydown(event) {
                if(event.key === 'Enter' && this.currentSubNav !== null) {
                    this.currentSubNav = null;
                }
            },
            showOverlay() {
                this.$store.commit('showOverlay');
            },

            goToAnchorInUrl() {
                if (window.location.hash) {
                    let anchorId = window.location.hash.substring(1);
                    document.getElementById(anchorId).scrollIntoView();
                    this.$store.commit("scrollToView", anchorId);
                }                
            },
        }
    }
</script>
